import { FIELD_WORDS, PATH_RE, actionFields, fieldSources, type FieldSource, type MappingField } from "@/lib/triggers";
import type { AdminForm, Trigger } from "@/lib/types";

/**
 * Mapping a request's keys onto the contact's custom fields (ADR-119 in platform): the arithmetic
 * behind the trigger page's Custom fields card and its mapper. Pure, for `test/field-mapping.test.ts`.
 *
 * core-api keeps the mapping as `mapping.fields`: the contact field's key → where the request carries
 * its answer, either a dotted path (`contact.vehicle`) or `{ name }`, a top-level key spelled exactly as
 * the sender spells it. The name form exists because some senders post a field under its label rather
 * than a key — GoHighLevel sends `"Date of Accident*": "..."` — and a path cannot spell spaces or
 * punctuation. core-api finds a name exactly, else as the one key equal once spaces, quote style and
 * capitals are evened out (`nameKey`), and never by a guess.
 *
 * The mapper works the other way round from the stored shape: it lists the keys the newest request
 * carried, in the sender's order, and for each one the contact field it fills, so mapping is done by
 * reading what arrived rather than typing. A field the trigger already maps from a key the newest
 * request did not carry is listed too, as "not in the newest request": that is what a renamed label
 * looks like, and the new label is suggested that same field. Suggestions pair a key with a field by
 * title: exactly, closely (the same words once underscores, punctuation and capitals are ignored) or
 * likely (most of the words the same); only the first two are taken in bulk.
 */

export { fieldSources, type FieldSource };

/** `trigger_mapping.MAX_FIELD_SOURCES` and `MAX_NAME`. */
export const MAX_FIELD_SOURCES = 200;
export const MAX_KEY_LENGTH = 200;
/** Below this, most-of-the-words-the-same is a coincidence. */
export const LIKELY_THRESHOLD = 0.6;

export type Confidence = "exact" | "close" | "likely";
const RANK: Record<Confidence, number> = { exact: 0, close: 1, likely: 2 };

export interface Suggestion {
  field: string;
  confidence: Confidence;
}

/** One of the contact's custom fields, as the mapper offers it. */
export interface MapperField {
  key: string;
  title: string;
  type: string;
  /** The folder it is in. */
  section: string;
  /** The key with the object in front, as GoHighLevel's Fields page writes one: `contact.phone_country`.
   *  Shown, never stored: the platform's key is `phone_country`, and that is what the API takes. */
  qualified: string;
}

/** The object a form is about, as a prefix reads it: the contact form's fields are `contact.…`, the firm
 *  form's `firm.…`. */
export function objectName(entity: string | undefined): string {
  return entity === "firm" ? "firm" : "contact";
}

export function qualifiedKey(entity: string | undefined, key: string): string {
  return `${objectName(entity)}.${key}`;
}

/** Where a row came from: the newest request, the stored mapping alone (its key was not in the
 *  request), or typed by hand in this sitting. */
export type RowOrigin = "request" | "stored" | "typed";

/**
 * What is known of the newest request's body: in hand (`read`), nothing received yet (`none`), emptied
 * by the retention period (`gone`: core-api keeps a settled request's JSON for 30 days by default,
 * then serves `raw: null`), or not fetched because the inbox or the request could not be read
 * (`unreadable`). Only a body in hand says which mapped keys a sender stopped posting.
 */
export type RequestState = "read" | "none" | "gone" | "unreadable";

/** The state from what has loaded: a failure, how many requests the inbox holds, and the newest one's
 *  body (undefined until it is read). Undefined while there is not yet enough to tell. */
export function requestState(loaded: {
  failed: boolean;
  count: number | undefined;
  raw: Record<string, unknown> | null | undefined;
}): RequestState | undefined {
  if (loaded.failed) return "unreadable";
  if (loaded.count === undefined) return undefined;
  if (loaded.count === 0) return "none";
  if (loaded.raw === undefined) return undefined;
  return loaded.raw === null ? "gone" : "read";
}

/** One key of a request, and what the trigger does with it. */
export interface MapperRow {
  /** The key as the sender writes it: a top-level key, or a dotted path to a nested value. */
  key: string;
  /** A path into the request rather than a top-level key: stored as a path, not a name. */
  nested: boolean;
  origin: RowOrigin;
  /** The newest request carried a value there, not a blank. */
  filled: boolean;
  /** One of the trigger's own entries already reads this key ("Phone number"), so it is not a custom field. */
  usedBy: string | null;
  /** The contact field it fills. */
  field: string | null;
  /** The field it could fill, when it fills none. */
  suggestion: Suggestion | null;
}

export interface PayloadKey {
  key: string;
  nested: boolean;
  filled: boolean;
}

// --- names, as core-api compares them ------------------------------------------------------------

/** How two names compare when they are not the same text: `trigger_mapping.name_key`. Unicode evened
 *  out, plain quotes, one space between words and none at either end, and case ignored. */
export function nameKey(name: string): string {
  return plainQuotes(name).split(/\s+/).filter(Boolean).join(" ").toLowerCase();
}

function plainQuotes(text: string): string {
  return text
    .normalize("NFKC")
    .replace(/[‘’‛′]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/ /g, " ");
}

/** Looser than `nameKey`, for suggestions only: underscores, hyphens and dots read as spaces, and
 *  punctuation is dropped, so `postal_code` and `Postal code` are one, and `Date of Accident*` and
 *  `Date of Accident` are one. Never used to store anything. */
export function matchKey(text: string): string {
  return plainQuotes(text)
    .toLowerCase()
    .replace(/[_\-./]+/g, " ")
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
}

function words(text: string): Set<string> {
  return new Set(
    matchKey(text)
      .split(" ")
      .filter(Boolean)
      // A light stem: "happens" and "happen" are one word, "drivers" and "driver" are one.
      .map((word) => (word.length > 3 && word.endsWith("s") ? word.slice(0, -1) : word)),
  );
}

/** How much of two texts' words are shared, 0 to 1 (Dice). */
export function similarity(a: string, b: string): number {
  const left = words(a);
  const right = words(b);
  if (left.size === 0 || right.size === 0) return 0;
  let shared = 0;
  for (const word of left) if (right.has(word)) shared += 1;
  return (2 * shared) / (left.size + right.size);
}

// --- what a request carries ------------------------------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Every key of a request a custom field can be filled from, in the sender's order: a top-level key
 * whose value is not an object (a name), and for an object or a list of objects, the dotted paths to
 * the values inside (paths, into the first items of a list). Keys only, never a value.
 */
export function payloadKeys(doc: unknown): PayloadKey[] {
  if (!isObject(doc)) return [];
  const out: PayloadKey[] = [];
  const visit = (node: unknown, path: string) => {
    if (isObject(node)) {
      for (const [key, value] of Object.entries(node)) visit(value, `${path}.${key}`);
    } else if (Array.isArray(node) && node.some(isObject)) {
      node.slice(0, 5).forEach((item, index) => visit(item, `${path}[${index}]`));
    } else if (PATH_RE.test(path)) {
      out.push({ key: path, nested: true, filled: hasValue(node) });
    }
  };
  for (const [key, value] of Object.entries(doc)) {
    if (isObject(value) || (Array.isArray(value) && value.some(isObject))) visit(value, key);
    else out.push({ key, nested: false, filled: hasValue(value) });
  }
  return out;
}

function stripPrefix(path: string): string {
  return path.replace(/^\$\./, "");
}

/** The request keys the trigger's own entries read, each with the entry's name: `phone` → "Phone
 *  number", an attribute's path → the attribute. Those are not custom fields, and the mapper says so. */
export function standardUses(trigger: Pick<Trigger, "action" | "mapping">): Record<string, string> {
  const mapping = trigger.mapping ?? {};
  const out: Record<string, string> = {};
  for (const field of actionFields(trigger.action)) {
    const path = mapping[field as MappingField];
    if (typeof path === "string" && path) out[stripPrefix(path)] = FIELD_WORDS[field].label;
  }
  const attributes = mapping.attributes;
  if (isObject(attributes)) {
    for (const [name, path] of Object.entries(attributes)) {
      if (typeof path === "string" && path) out[stripPrefix(path)] = `the attribute ${name}`;
    }
  }
  if (Array.isArray(mapping.idempotency)) {
    for (const path of mapping.idempotency) {
      if (typeof path === "string" && path) out[stripPrefix(path)] = "what a request is known by";
    }
  }
  return out;
}

/** Whether a stored source is where `key` is: a path exactly, a name as core-api compares names. */
export function sourceReads(source: FieldSource, key: string): boolean {
  return typeof source === "string" ? stripPrefix(source) === key : nameKey(source.name) === nameKey(key);
}

/** A stored source as one line, for a row the newest request did not carry. */
export function sourceKey(source: FieldSource): string {
  return typeof source === "string" ? stripPrefix(source) : source.name;
}

// --- the contact's fields ----------------------------------------------------------------------

/** The custom fields a key can fill, in the form's order: active fields whose value is the form's own.
 *  A platform field (first name, phone, email) is written by the trigger's own entry, so it is left out. */
export function mapperFields(design: Pick<AdminForm, "sections" | "entity"> | undefined): MapperField[] {
  const out: MapperField[] = [];
  for (const section of design?.sections ?? []) {
    if (section.archived) continue;
    for (const field of section.fields) {
      if (field.archived || (field.storage !== "value" && field.storage !== "accident")) continue;
      out.push({
        key: field.key,
        title: field.title,
        type: field.type,
        section: section.title,
        qualified: qualifiedKey(design?.entity, field.key),
      });
    }
  }
  return out;
}

/**
 * The field a key most plausibly fills, out of those not `taken`: the one whose title is the key
 * exactly; else the one whose title or key is the same once underscores, punctuation and capitals
 * are ignored; else the one sharing most of its words, when it shares enough and clearly more than
 * the runner-up. Two fields that fit equally well mean no suggestion: never a guess.
 */
export function suggest(key: string, fields: readonly MapperField[], taken: ReadonlySet<string> = new Set()): Suggestion | null {
  const open = fields.filter((field) => !taken.has(field.key));
  const exact = open.filter((field) => field.title === key);
  const exactOne = exact.length === 1 ? exact[0] : byOwnKey(key, exact);
  if (exactOne) return { field: exactOne.key, confidence: "exact" };
  if (exact.length > 1) return null;
  const wanted = matchKey(key);
  if (!wanted) return null;
  const close = open.filter((field) => matchKey(field.title) === wanted || matchKey(field.key) === wanted);
  const closeOne = close.length === 1 ? close[0] : byOwnKey(key, close);
  if (closeOne) return { field: closeOne.key, confidence: "close" };
  if (close.length > 1) return null;
  let best: MapperField | null = null;
  let bestScore = 0;
  let second = 0;
  for (const field of open) {
    const score = Math.max(similarity(key, field.title), similarity(key, field.key));
    if (score > bestScore) {
      second = bestScore;
      bestScore = score;
      best = field;
    } else if (score > second) {
      second = score;
    }
  }
  if (!best || bestScore < LIKELY_THRESHOLD || bestScore - second < 0.1) return null;
  return { field: best.key, confidence: "likely" };
}

/** Of several fields a key fits equally by title, the one whose own key is that key (the contact's
 *  `city`, not the company's `business_city`, both titled City), else the one whose key is that key
 *  with the form's object in front (`state` and `contact_state`, as GoHighLevel writes
 *  `contact.state`); none when neither singles one out. */
function byOwnKey(key: string, tied: readonly MapperField[]): MapperField | null {
  if (tied.length < 2) return null;
  const wanted = matchKey(key);
  const own = tied.filter((field) => matchKey(field.key) === wanted);
  if (own.length === 1) return own[0] ?? null;
  const prefixed = tied.filter((field) => matchKey(field.key) === matchKey(`${field.qualified.split(".")[0]} ${key}`));
  return prefixed.length === 1 ? (prefixed[0] ?? null) : null;
}

/** The fields the rows fill, leaving out a stored row's: its key is gone from the request, so the
 *  key that replaced it may be suggested the same field. */
function takenFields(rows: readonly MapperRow[]): Set<string> {
  return new Set(rows.filter((row) => row.origin !== "stored").map((row) => row.field).filter((key): key is string => key !== null));
}

/** Suggestions recomputed for every row that fills no field and is not the trigger's own. A nested path
 *  gets only a sure one: `location.city` shares a word with the contact's City, but it is the sender's own
 *  address, and a likely match there would put the agency's city on every contact. */
export function withSuggestions(rows: readonly MapperRow[], fields: readonly MapperField[]): MapperRow[] {
  const taken = takenFields(rows);
  return rows.map((row) => {
    if (row.field !== null || row.usedBy !== null) return row.suggestion === null ? row : { ...row, suggestion: null };
    const found = suggest(row.key, fields, taken);
    return { ...row, suggestion: row.nested && found?.confidence === "likely" ? null : found };
  });
}

/** The rows with what the trigger's own entries read marked afresh, for a form whose paths are being
 *  edited alongside: a key an entry now reads is not a custom field, and one it stopped reading is. */
export function withUses(rows: readonly MapperRow[], uses: Record<string, string>, fields: readonly MapperField[]): MapperRow[] {
  const next = rows.map((row) => {
    const usedBy = row.origin === "stored" ? null : (uses[row.key] ?? null);
    return usedBy === row.usedBy ? row : { ...row, usedBy, field: usedBy === null ? row.field : null };
  });
  return withSuggestions(next, fields);
}

/**
 * The mapper's rows: every key of the newest request, in its order, with the field the trigger fills
 * from it (a name as core-api matches names, a path exactly) and a suggestion where it fills none;
 * then every mapped field whose key the request did not carry, as "not in the newest request".
 * `sources` is the trigger's `fields` entry as it stands, `uses` what its own entries read
 * (`standardUses`).
 */
export function buildRows(
  doc: unknown,
  sources: Record<string, FieldSource>,
  uses: Record<string, string>,
  design: Pick<AdminForm, "sections" | "entity"> | undefined,
): MapperRow[] {
  const fields = mapperFields(design);
  const entries = Object.entries(sources);
  const reads = (key: string, nested: boolean): string | null => {
    for (const [field, source] of entries) {
      if (typeof source === "string" ? stripPrefix(source) === key : !nested && sourceReads(source, key)) return field;
    }
    return null;
  };
  const rows: MapperRow[] = [];
  const taken = new Set<string>();
  for (const carried of payloadKeys(doc)) {
    const field = reads(carried.key, carried.nested);
    if (field !== null) taken.add(field);
    rows.push({ ...carried, origin: "request", usedBy: uses[carried.key] ?? null, field, suggestion: null });
  }
  for (const [field, source] of entries) {
    if (taken.has(field)) continue;
    rows.push({
      key: sourceKey(source),
      nested: typeof source === "string",
      origin: "stored",
      filled: false,
      usedBy: null,
      field,
      suggestion: null,
    });
  }
  return withSuggestions(rows, fields);
}

// --- edits -------------------------------------------------------------------------------------

/** Row `index` fills `field` (null: none). A field fills from one key, so another row filling it lets
 *  go of it, and a stored row that let go of its only reason to be listed is gone. */
export function pickField(rows: readonly MapperRow[], index: number, field: string | null, fields: readonly MapperField[]): MapperRow[] {
  const next = rows
    .map((row, i) => {
      if (i === index) return { ...row, field };
      if (field !== null && row.field === field) return { ...row, field: null };
      return row;
    })
    .filter((row) => !(row.origin === "stored" && row.field === null));
  return withSuggestions(next, fields);
}

/** Which rows `acceptSuggestions` would fill, in row order, each field once. */
function acceptable(rows: readonly MapperRow[], upTo: Confidence): MapperRow[] {
  const taken = takenFields(rows);
  const out: MapperRow[] = [];
  for (const row of rows) {
    const suggestion = row.suggestion;
    if (row.field !== null || !suggestion || RANK[suggestion.confidence] > RANK[upTo] || taken.has(suggestion.field)) continue;
    taken.add(suggestion.field);
    out.push(row);
  }
  return out;
}

/** Every suggestion up to `upTo` taken. */
export function acceptSuggestions(rows: readonly MapperRow[], fields: readonly MapperField[], upTo: Confidence = "close"): MapperRow[] {
  let next: MapperRow[] = [...rows];
  for (const row of acceptable(rows, upTo)) {
    const index = next.findIndex((r) => r.origin === row.origin && r.key === row.key && r.nested === row.nested);
    if (index >= 0 && row.suggestion) next = pickField(next, index, row.suggestion.field, fields);
  }
  return next;
}

/** How many suggestions `acceptSuggestions` would take. */
export function acceptableCount(rows: readonly MapperRow[], upTo: Confidence = "close"): number {
  return acceptable(rows, upTo).length;
}

/** Row `index` fills nothing. A key the request carried, or one typed, stays listed; a stored one is gone. */
export function clearRow(rows: readonly MapperRow[], index: number, fields: readonly MapperField[]): MapperRow[] {
  const row = rows[index];
  if (!row) return [...rows];
  const next =
    row.origin === "stored" ? rows.filter((_, i) => i !== index) : rows.map((r, i) => (i === index ? { ...r, field: null } : r));
  return withSuggestions(next, fields);
}

/** A key with a dot or an index that is a path into the request. Anything else is a top-level key,
 *  whatever characters it has: that is the whole point of a name. */
export function looksNested(key: string): boolean {
  return /[.[]/.test(key) && PATH_RE.test(key);
}

/** Why `text` cannot be a key typed by hand, or null. */
export function keyProblem(text: string, rows: readonly MapperRow[]): string | null {
  const key = text.trim();
  if (!key) return "Type the key as the sender writes it.";
  if (key.length > MAX_KEY_LENGTH) return `A key is up to ${MAX_KEY_LENGTH} characters.`;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(key)) return "A key is on one line.";
  const nested = looksNested(key);
  if (rows.some((row) => (nested || row.nested ? row.key === key : nameKey(row.key) === nameKey(key)))) return "That key is already listed.";
  return null;
}

/** A key typed by hand, for a sender that has not posted yet or a key its newest request left out. */
export function addKey(rows: readonly MapperRow[], text: string, fields: readonly MapperField[]): MapperRow[] {
  const key = text.trim();
  const row: MapperRow = { key, nested: looksNested(key), origin: "typed", filled: false, usedBy: null, field: null, suggestion: null };
  return withSuggestions([...rows, row], fields);
}

// --- what is saved -------------------------------------------------------------------------------

/** The `fields` entry the rows make. A field whose key is the one it was stored with keeps its stored
 *  source, so saving changes nothing it does not have to; a new pairing is a path for a nested key
 *  and `{ name }` for a top-level one. */
export function mappingFromRows(rows: readonly MapperRow[], original: Record<string, FieldSource>): Record<string, FieldSource> {
  const out: Record<string, FieldSource> = {};
  for (const row of rows) {
    if (row.field === null) continue;
    const prior = original[row.field];
    out[row.field] = prior !== undefined && sourceReads(prior, row.key) ? prior : row.nested ? row.key : { name: row.key };
  }
  return out;
}

/** The trigger's whole mapping with the rows' `fields` in place of the stored one, as `PUT /triggers/{id}`
 *  takes it: the other entries as they are, and no `fields` key at all when nothing is mapped. */
export function mappingWithFields(mapping: Record<string, unknown> | undefined, fields: Record<string, FieldSource>): Record<string, unknown> {
  const { fields: _stored, ...rest } = mapping ?? {};
  return Object.keys(fields).length > 0 ? { ...rest, fields } : rest;
}

/** Why the rows cannot be saved, or null. */
export function rowsProblem(rows: readonly MapperRow[]): string | null {
  const mapped = rows.filter((row) => row.field !== null);
  if (mapped.length > MAX_FIELD_SOURCES) return `A trigger fills at most ${MAX_FIELD_SOURCES} custom fields.`;
  return null;
}

/** JSON with every object's keys in order, to tell two mappings apart. */
export function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, node: unknown) =>
    node && typeof node === "object" && !Array.isArray(node)
      ? Object.fromEntries(Object.entries(node as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : node,
  );
}

// --- what the card says --------------------------------------------------------------------------

export interface MapperCounts {
  /** Keys that fill a field. */
  mapped: number;
  /** Keys (the request's, or typed) that fill nothing and are not the trigger's own. */
  unmapped: number;
  /** Of the unmapped, how many have a suggestion. */
  suggested: number;
  /** Mapped keys the newest request did not carry. */
  missing: number;
  /** Keys read by the trigger's own entries. */
  own: number;
}

/** `compared` is whether the newest request's body was there to compare with (`RequestState` read):
 *  without it, a stored key is not known to be missing from anything. */
export function mapperCounts(rows: readonly MapperRow[], compared = true): MapperCounts {
  const out: MapperCounts = { mapped: 0, unmapped: 0, suggested: 0, missing: 0, own: 0 };
  for (const row of rows) {
    if (row.field !== null) {
      out.mapped += 1;
      if (compared && row.origin === "stored") out.missing += 1;
    } else if (row.usedBy !== null) {
      out.own += 1;
    } else {
      out.unmapped += 1;
      if (row.suggestion) out.suggested += 1;
    }
  }
  return out;
}

/** What is odd about a key as written, to show beside it: a space at either end is invisible and
 *  easy to lose when the key is typed again elsewhere. */
export function keyNotes(key: string): string[] {
  const notes: string[] = [];
  if (key !== key.trimEnd()) notes.push("ends with a space");
  if (key !== key.trimStart()) notes.push("starts with a space");
  if (/[‘’“”]/.test(key)) notes.push("curly quote");
  return notes;
}

export type MapperFilter = "all" | "unmapped" | "mapped" | "missing";

export function filterRows(rows: readonly MapperRow[], filter: MapperFilter, search: string): MapperRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filter === "unmapped" && (row.field !== null || row.usedBy !== null)) return false;
    if (filter === "mapped" && row.field === null) return false;
    if (filter === "missing" && (row.field === null || row.origin !== "stored")) return false;
    if (!needle) return true;
    return `${row.key} ${row.field ?? ""} ${row.suggestion?.field ?? ""} ${row.usedBy ?? ""}`.toLowerCase().includes(needle);
  });
}
